import cx from 'classnames';
import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { plural } from '../../../../shared/helpers';
import Locator from '../../../../shared/Locator';
import { isFreeTier } from '../../../../shared/subscriptions';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useDresser } from '../../../contexts/DrawerContext';
import { useQuery } from '../../../contexts/RouterContext';
import { useAddToast } from '../../../contexts/ToastContext';
import { window } from '../../../globals';
import { errorMessageFromResponse } from '../../../helpers/errors';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { BUTTON_SIZE, Icon, icons } from '../../generic';
import BulkSelectionBanner from '../../generic/BulkSelectionBanner';
import DropdownButton from '../../generic/DropdownButton';
import Checkbox from '../../generic/Forms/Checkbox';
import List from '../../generic/List';
import ListRowLoading from '../../generic/List/ListRowLoading';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import Text from '../../generic/Text';
import Tooltip from '../../generic/Tooltip';
import type { IssueExceptionsForDashboard, IssueExceptionsRemoveRequest } from '../../../../shared/issuesV2';
import type { FailedFetchResponse } from '../../../helpers/errors';

import ExceptionTitleContent from './ExceptionTitleContent';
import ExtendExceptionModal from './ExtendExceptionModal';
import IssueExceptionsDrawer from './IssueExceptionsDrawer';
import IssueExceptionsTableIgnoreScopeBadge from './IssueExceptionsTableIgnoreScopeBadge';
import { canRemoveException } from './permissions';
import { getHumanReadableVersion } from './utils';
import type { UseIssueExceptionsProps, UseIssueExceptionsResponse } from './useIssueExceptions';

import styles from './IssueExceptionsTable.module.less';

/**
 * Returns the column names for the issue exceptions table which can optionally include package labels.
 */
const getColumnNames = (hasPackageLabels: boolean) => {
  if (hasPackageLabels) {
    return [
      'Issue ignored',
      'Package',
      'Version',
      'Ignore Scope',
      'Package Label',
      'Created By',
      'Note',
    ];
  }
  return [
    'Issue ignored',
    'Package',
    'Version',
    'Ignore Scope',
    'Created By',
    'Note',
  ];
};

type IssueExceptionsQueryParams = {
  selectedExceptionId?: number;
};

type IssueExceptionsTableProps = UseIssueExceptionsProps & Pick<
  UseIssueExceptionsResponse,
  'page' | 'count' | 'setPage' | 'setCount' | 'onRemoveExceptions' | 'onExtendException'
>;

/**
 * Renders a basic table containing policy exceptions
 */
const IssueExceptionsTable = ({
  projectId,
  releaseGroupId,
  category,
  page,
  count,
  setPage,
  setCount,
  onRemoveExceptions,
  onExtendException,
}: IssueExceptionsTableProps) => {
  const { onConfirm } = useDialogContext();
  const addToast = useAddToast();
  const [queryParams, setQueryParams] = useQuery<IssueExceptionsQueryParams>();
  const { pushDrawer } = useDresser();

  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());
  const [isSelectAllEnabled, setSelectAllEnabled] = useState<boolean>(false);
  const [modalException, setModalException] = useState<IssueExceptionsForDashboard | null>(null);

  const exceptionsData = useSelector(IssuesV2Store.selectors.getIssueExceptions);
  const totalCount = useSelector(IssuesV2Store.selectors.getIssueExceptionsCount);
  const { isLoading } = useSelector(IssuesV2Store.selectors.getAsyncStatusIssueExceptions);

  // package labels are only available to paid orgs
  const isFreeOrg = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });
  const columnNames = getColumnNames(!isFreeOrg);

  const areAllExceptionsOnPageSelected =
    (exceptionsData.length > 0 && exceptionsData.every((exception) => checkedItems.has(exception.id))) ||
      isSelectAllEnabled;

  const isCheckboxIndeterminate = !areAllExceptionsOnPageSelected &&
    (exceptionsData.length > 0 && exceptionsData.some((exception) => checkedItems.has(exception.id)));

  const openDrawer = useCallback(() =>
    pushDrawer(
      <IssueExceptionsDrawer
        onOpenExtendExceptionModal={setModalException}
        onRemoveExceptions={onRemoveExceptions}
      />,
      {
        onClose: () => setQueryParams({ selectedExceptionId: undefined }),
      },
    ), [pushDrawer, setModalException, onRemoveExceptions, setQueryParams]);

  //  Open the drawer when the selectedExceptionId changes.
  useEffect(() => {
    if (queryParams.selectedExceptionId) {
      openDrawer();
    }
  }, [openDrawer, queryParams.selectedExceptionId]);

  const onDeleteExceptions = () => {
    let removeCriteria: IssueExceptionsRemoveRequest;
    const filters = { projectId, releaseGroupId, category };
    if (isSelectAllEnabled) {
      removeCriteria = {
        kind: 'all',
        filters,
      };
    } else {
      removeCriteria = {
        kind: 'idList',
        value: Array.from(checkedItems),
      };
    }

    onConfirm({
      title: 'Remove rules?',
      body: `
        ${ruleCountStr} matching the applied filters will be removed. Issue scans will be triggered for affected scopes to re-evaluate the remaining issue exceptions, and may take a few minutes to complete.
      `,
      onConfirm: async () => {
        try {
          const deletedCount = await onRemoveExceptions(removeCriteria);

          addToast({
            content: `
              ${deletedCount ?? 0} Ignore ${plural(deletedCount, 'rule')} removed. Issue scans will be triggered for affected scopes to re-evaluate the remaining issue exceptions.
            `,
            type: 'success',
          });

          setCheckedItems(new Set());
          setSelectAllEnabled(false);
        } catch (error) {
          const message = errorMessageFromResponse(error as FailedFetchResponse);
          addToast({ content: message, type: 'error' });
        }
      },
    });
  };

  const onSelectAllRulesOnPage = () => {
    if (areAllExceptionsOnPageSelected || isCheckboxIndeterminate) {
      const checkedItemsWithoutCurrentPage = new Set(checkedItems);
      exceptionsData.forEach((exception) => checkedItemsWithoutCurrentPage.delete(exception.id));
      setCheckedItems(checkedItemsWithoutCurrentPage);
      setSelectAllEnabled(false);
    } else {
      const newCheckedItems = new Set(checkedItems);
      exceptionsData.forEach((exception) => newCheckedItems.add(exception.id));
      setCheckedItems(newCheckedItems);
    }
  };

  const onCheckItem = (id: number) => {
    let newCheckedItems = new Set<number>();

    // if select all is enabled, and user clicks on a single item, disable select all, and check every item except the selected one
    if (isSelectAllEnabled) {
      exceptionsData.forEach((exception) => {
        if (exception.id !== id) {
          newCheckedItems.add(exception.id);
        }
      });
    } else {
      newCheckedItems = new Set(checkedItems);
      if (checkedItems.has(id)) {
        newCheckedItems.delete(id);
      } else {
        newCheckedItems.add(id);
      }
    }

    setCheckedItems(newCheckedItems);
    setSelectAllEnabled(false);
  };

  //  Checks if the user has permission to remove the exception, and if so, checks the item.
  const onCheckItemWithPermissionCheck = (exception: IssueExceptionsForDashboard) => {
    if (canRemoveException(exception)) {
      onCheckItem(exception.id);
    }
  };

  //  Clicking a row will open the drawer for that exception.
  //  The effect above opens the drawer when the selectedExceptionId changes.
  const onClickRow = (selectedExceptionId: number) => setQueryParams({ selectedExceptionId });

  const onSelectAllItems = () => {
    setSelectAllEnabled(true);
  };

  const onClearAllItems = () => {
    setSelectAllEnabled(false);
    setCheckedItems(new Set());
  };

  let ruleCountStr = 'No rules';
  if (isSelectAllEnabled) {
    ruleCountStr = 'All rules';
  } else if (checkedItems.size > 0) {
    ruleCountStr = `${checkedItems.size} ${plural(checkedItems.size, 'rule')}`;
  }

  return (
    <>
      <List className={cx(styles.issueExceptionsTable, { [styles.issueExceptionsTable__loading]: isLoading })}>
        <List.Header hasCheckbox>
          {/* Action bar */}
          <div className={cx(styles.issueExceptionsTable__headerAction)}>
            <div>
              <Checkbox
                isChecked={isSelectAllEnabled || areAllExceptionsOnPageSelected}
                isIndeterminate={isCheckboxIndeterminate}
                onChange={onSelectAllRulesOnPage}
                isRippleDisabled
              />
              <span className={styles.issueExceptionsTable__headerAction__title}>{ruleCountStr} selected</span>
            </div>
            <div className={cx(styles.issueExceptionsTable__headerAction__actionMenu)}>
              <DropdownButton
                id="issue-exceptions-actions"
                title={<div>Actions <Icon icon={icons.caretDown} /></div>}
                size={BUTTON_SIZE.SMALL}
                disabled={checkedItems.size === 0}
                pullRight
                noCaret
              >
                <DropdownButton.Item key="remove" eventKey="remove" onSelect={onDeleteExceptions}>
                  Delete rules
                </DropdownButton.Item>
              </DropdownButton>
            </div>
          </div>
        </List.Header>
        <div className={cx(styles.issueExceptionsTable__header)}>
          {/* Empty column for checkbox */}
          <span />
          {columnNames.map((name) => (
            <span>{name}</span>
          ))}
        </div>
        <List.Body>
          {isLoading && _.times(count, () => <ListRowLoading />)}
          {!isLoading && exceptionsData.length === 0 && (
            <List.RowContainer>
              <List.Row>
                <div className={cx(styles.issueExceptionsTable__noResults)}>
                  <Text.Heading>No rules found</Text.Heading>
                </div>
              </List.Row>
            </List.RowContainer>
          )}
          {!isLoading &&
            <>
              {areAllExceptionsOnPageSelected && (
                <BulkSelectionBanner
                  itemsNoun="rule"
                  sectionName="rules"
                  totalItemsCount={totalCount}
                  areAllItemsSelected={isSelectAllEnabled || checkedItems.size === totalCount}
                  pageSize={exceptionsData.length}
                  onSelectAll={onSelectAllItems}
                  onClearSelection={onClearAllItems}
                />
              )}
              {exceptionsData.map((exception) => {
                const isRowDisabled = !canRemoveException(exception);
                const isExpired = exception.isExpired;

                return (
                  // Show the row as grayed out if the whole thing is disabled or if the exception is expired.
                  // We only check for `isRowDisabled` later on for preventing interactions, so this allows us to still interact
                  // with expired exceptions.
                  <List.RowContainer isDisabled={isRowDisabled || isExpired}>
                    <List.Row hasCheckbox onClick={() => onClickRow(exception.id)}>
                      <span className={cx(styles.issueExceptionsTable__row)} key={exception.id}>
                        {/* Checkbox */}
                        <span className={cx(styles.exceptionColumn)}>
                          <Tooltip
                            id={`exception-checkbox-tooltip-${exception.id}`}
                            showTooltip={isRowDisabled}
                            tooltip="You do not have permission to modify this rule"
                          >
                            {/* This must be wrapped in a span for the tooltip to work properly */}
                            <span>
                              <Checkbox
                                isChecked={isSelectAllEnabled || checkedItems.has(exception.id)}
                                isDisabled={isRowDisabled}
                                onChange={() => onCheckItemWithPermissionCheck(exception)}
                                shouldStopPropagation
                              />
                            </span>
                          </Tooltip>
                        </span>

                        {/* Issue ignored */}
                        <ExceptionTitleContent
                          exception={exception}
                          setModalException={setModalException}
                          hasPermissionToExtend={!isRowDisabled}
                        />

                        {/* Package */}
                        <span className={cx(styles.exceptionColumn)}>{exception.dependencyTitle}</span>

                        {/* Version */}
                        <span className={cx(styles.exceptionColumn)}>
                          {getHumanReadableVersion(
                            exception.packageScope,
                            Locator.parse(exception.dependencyRevisionLocator || ''),
                          )}
                        </span>

                        {/* Scope */}
                        <span className={cx(styles.exceptionColumn)}>
                          <IssueExceptionsTableIgnoreScopeBadge
                            ignoreScope={exception.ignoreScope}
                            projectTitle={exception.projectTitle}
                            policyTitle={exception.policyTitle}
                          />
                        </span>

                        {/* Package Labels */}
                        <span className={cx(styles.exceptionColumn)}>{exception.packageLabel}</span>

                        {/* By */}
                        <span className={cx(styles.exceptionColumn)}>{exception.createdBy}</span>

                        {/* Note */}
                        <span className={cx(styles.exceptionColumn)}>
                          <Tooltip
                            id="exception-note-tooltip"
                            tooltip={exception.note}
                            showTooltip={!!exception.note}
                            placement="left"
                          >
                            <span>{exception.note}</span>
                          </Tooltip>
                        </span>
                      </span>
                    </List.Row>
                  </List.RowContainer>
                );
              })}
            </>}
        </List.Body>
      </List>
      <PaginationFooter page={page} count={count} totalItems={totalCount} setPage={setPage} setCount={setCount} />
      <ExtendExceptionModal
        exception={modalException as IssueExceptionsForDashboard}
        isShowing={!!modalException}
        onHide={() => setModalException(null)}
        onExtendException={onExtendException}
      />
    </>
  );
};

export default IssueExceptionsTable;
